/**
 * Revoked-integrations set (TV1-2446 slice 7, design D4/D5 banner)
 *
 * Pure reduction of the `ExternalSystemsIntegrations` subscription stream onto the set of
 * integrations whose credential is currently revoked, of ANY kind (sales channel, ERP,
 * invoicing, logistics, messaging) — unlike `channelsSyncPolicy`, which filters to
 * `connectorKind: 'salesChannel'` only. The org-wide banner must warn about a revoked ERP or
 * invoicing connector just as much as a revoked sales channel.
 *
 * Kept dependency-free of the CDSE store so the reduction itself is directly testable against
 * a scripted snapshot + update/delete sequence, per the slice's oracle.
 */

import type { ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import type { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import { channelDisplayName } from '../channelDisplayName';

export type RevokedIntegrationEntry = {
  integrationId: string;
  /** `label`, falling back to `channelUserName`, falling back to `connectorId` — see `channelDisplayName`. */
  label: string;
};

/**
 * `credentialState` is ABSENT for an integration that was never revoked, and `'valid'` once a
 * revoked one is restored (the readmodel's `SystemIntegrationCredentialRestored` projector).
 *
 * The SINGLE definition of "revoked" — every site that needs it (the banner selector, the
 * channel-detail Reconnect gate, the batch-process Retry read) calls this rather than comparing
 * `credentialState` itself, so the comparison cannot diverge (e.g. into `!== 'valid'`, which
 * would misclassify an integration that has never had a `credentialState` at all).
 *
 * Typed against a plain `{ credentialState?: string }` — wider than
 * `Pick<SystemIntegrationsReadmodel, 'credentialState'>` — so it also fits the batch-process
 * read model's separately-shaped fetch result without a cast.
 */
export const isIntegrationCredentialRevoked = (
  item: { credentialState?: string }
): boolean => item.credentialState === 'revoked';

const toEntry = ( item: SystemIntegrationsReadmodel ): RevokedIntegrationEntry => ({
  integrationId: item.id,
  label: channelDisplayName( item )
});

/** Builds the revoked set from a subscription snapshot. */
export const applyRevokedIntegrationsSnapshot = (
  items: SystemIntegrationsReadmodel[]
): Record<string, RevokedIntegrationEntry> => {
  const result: Record<string, RevokedIntegrationEntry> = {};
  items.forEach(( item ) => {
    if ( isIntegrationCredentialRevoked( item )) result[item.id] = toEntry( item );
  });
  return result;
};

/**
 * Folds ONE subscription change onto the current revoked set.
 *
 * - `snapshot` replaces the set wholesale.
 * - `insert`/`update` adds the row when its credential is revoked, and REMOVES it when not —
 *   this is how a Credential Restoration clears the banner live: the restoring event projects
 *   `credentialState: 'valid'`, the row arrives as a plain `update`, and it must stop matching.
 * - `delete` removes the row unconditionally (an integration deletion also ends the revocation).
 * - `signal` is a no-op (no payload change to react to).
 */
export const reduceRevokedIntegrationsChange = (
  current: Record<string, RevokedIntegrationEntry>,
  change: ReadmodelChange<SystemIntegrationsReadmodel>
): Record<string, RevokedIntegrationEntry> => {
  if ( change.type === 'signal' ) return current;
  if ( change.type === 'snapshot' ) return applyRevokedIntegrationsSnapshot( change.data.items );

  if ( change.type === 'delete' ) {
    if ( !( change.previous.id in current )) return current;
    const next = { ...current };
    delete next[change.previous.id];
    return next;
  }

  // insert | update
  const item = change.data;
  const wasPresent = item.id in current;
  if ( isIntegrationCredentialRevoked( item )) {
    return { ...current, [item.id]: toEntry( item ) };
  }
  if ( !wasPresent ) return current;
  const next = { ...current };
  delete next[item.id];
  return next;
};

/** Revoked entries, sorted by label for stable rendering. */
export const selectSortedRevokedIntegrations = (
  entries: Record<string, RevokedIntegrationEntry>
): RevokedIntegrationEntry[] =>
  Object.values( entries ).sort(( a, b ) => a.label.localeCompare( b.label, undefined, { sensitivity: 'base' }));
